import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider, useSelector, useDispatch } from "react-redux";
import { Toaster } from "sonner";
import { store } from "./store";
import { setAuth, clearAuth } from "./store/authSlice";
import api from "./utils/api";
import BetaGatePage, { hasBetaAccess } from "./pages/auth/BetaGatePage";

import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardPage from "./pages/dashboard/DashboardPage";
import OnboardingWizard from "./components/OnboardingWizard";
import { GlossaryDrawerProvider } from "./components/shared/GlossaryDrawer/GlossaryDrawer";

/* Lazy-loaded pages for performance */
const IntegrationsPage = lazy(() => import("./pages/integrations/IntegrationsPage"));
const SubscriptionsPage = lazy(() => import("./pages/subscriptions/SubscriptionsPage"));
const ForecastingPage = lazy(() => import("./pages/forecasting/ForecastingPage"));
const AlertsPage = lazy(() => import("./pages/alerts/AlertsPage"));
const ReportsPage = lazy(() => import("./pages/reports/ReportsPage"));
const TeamPage = lazy(() => import("./pages/team/TeamPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const NotificationsPage = lazy(() => import("./pages/notifications/NotificationsPage"));
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage"));
const HelpPage = lazy(() => import("./pages/help/HelpPage"));
const ChatPage = lazy(() => import("./pages/chat/ChatPage"));
const FinancialsPage = lazy(() => import("./pages/financials/FinancialsPage"));
const AiAdvisorPage = lazy(() => import("./pages/ai-advisor/AiAdvisorPage"));
const PredictiveAnalysisPage = lazy(() => import("./pages/predictive/PredictiveAnalysisPage"));
const CrmPage = lazy(() => import("./pages/crm/CrmPage"));
const HrPage = lazy(() => import("./pages/hr/HrPage"));
const BankingPage = lazy(() => import("./pages/banking/BankingPage"));
const DataAnalyticsPage = lazy(() => import("./pages/data-analytics/DataAnalyticsPage"));
const BenchmarkingPage = lazy(() => import("./pages/benchmarking/BenchmarkingPage"));
const MetricGlossaryPage = lazy(() => import("./pages/glossary/MetricGlossaryPage"));
const CompliancePage = lazy(() => import("./pages/compliance/CompliancePage"));
const MetricApiDocsPage = lazy(() => import("./pages/api-docs/MetricApiDocsPage"));
/* Admin pages */
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAccounts = lazy(() => import("./pages/admin/AdminAccounts"));
const AdminAccountDetail = lazy(() => import("./pages/admin/AdminAccountDetail"));
const AdminBranding = lazy(() => import("./pages/admin/AdminBranding"));
const AdminTickets = lazy(() => import("./pages/admin/AdminTickets"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));

/* Partner Portal pages */
const PartnerLayout = lazy(() => import("./layouts/PartnerLayout"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const PartnerAccounts = lazy(() => import("./pages/partner/PartnerAccounts"));
const PartnerAccountView = lazy(() => import("./pages/partner/PartnerAccountView"));
const PartnerSettings = lazy(() => import("./pages/partner/PartnerSettings"));
const PartnerReports = lazy(() => import("./pages/partner/PartnerReports"));
const PartnerSubscription = lazy(() => import("./pages/partner/PartnerSubscription"));
const PartnerSupport = lazy(() => import("./pages/partner/PartnerSupport"));

function LazyPage({ children }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#CDD2DE", borderTopColor: "#1B75BA" }} /></div>}>
      {children}
    </Suspense>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, token, hydrated } = useSelector((s) => s.auth);
  // Wait for rehydration on hard refresh before deciding to redirect
  if (!hydrated) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="auth-hydrating">
        <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#CDD2DE", borderTopColor: "#1B75BA" }} />
      </div>
    );
  }
  if (!token || !user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const dispatch = useDispatch();
  const { token, user, hydrated } = useSelector((s) => s.auth);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("onpoint_token");
    if (saved && !token) {
      api
        .get("/auth/me")
        .then((res) => {
          const refresh = localStorage.getItem("onpoint_refresh");
          dispatch(setAuth({ user: res.data, token: saved, refreshToken: refresh }));
        })
        .catch(() => {
          localStorage.removeItem("onpoint_token");
          localStorage.removeItem("onpoint_refresh");
          dispatch(clearAuth());
        });
    } else if (!saved && !hydrated) {
      // No token to rehydrate - mark hydrated immediately so protected routes redirect
      dispatch(clearAuth());
    }
  }, []);

  // Show onboarding for first-time users
  useEffect(() => {
    if (user && token) {
      const onboarded = localStorage.getItem("onpoint_onboarded");
      if (!onboarded) {
        setShowOnboarding(true);
      }
    }
  }, [user, token]);

  return (
    <>
      {showOnboarding && <OnboardingWizard onComplete={() => setShowOnboarding(false)} />}
      <GlossaryDrawerProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* Public developer surface - no auth required so partners can preview */}
        <Route path="/developer/metrics" element={<LazyPage><MetricApiDocsPage /></LazyPage>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/integrations" element={<LazyPage><IntegrationsPage /></LazyPage>} />
          <Route path="/subscriptions" element={<LazyPage><SubscriptionsPage /></LazyPage>} />
          <Route path="/forecasting" element={<LazyPage><ForecastingPage /></LazyPage>} />
          <Route path="/alerts" element={<LazyPage><AlertsPage /></LazyPage>} />
          <Route path="/reports" element={<LazyPage><ReportsPage /></LazyPage>} />
          <Route path="/notifications" element={<LazyPage><NotificationsPage /></LazyPage>} />
          <Route path="/analytics" element={<LazyPage><AnalyticsPage /></LazyPage>} />
          <Route path="/help" element={<LazyPage><HelpPage /></LazyPage>} />
          <Route path="/chat" element={<LazyPage><ChatPage /></LazyPage>} />
          <Route path="/financials" element={<LazyPage><FinancialsPage /></LazyPage>} />
          <Route path="/ai-advisor" element={<LazyPage><AiAdvisorPage /></LazyPage>} />
          <Route path="/team" element={<LazyPage><TeamPage /></LazyPage>} />
          <Route path="/settings" element={<LazyPage><SettingsPage /></LazyPage>} />
          <Route path="/predictive" element={<LazyPage><PredictiveAnalysisPage /></LazyPage>} />
          <Route path="/crm" element={<LazyPage><CrmPage /></LazyPage>} />
          <Route path="/hr" element={<LazyPage><HrPage /></LazyPage>} />
          <Route path="/banking" element={<LazyPage><BankingPage /></LazyPage>} />
          <Route path="/data-analytics" element={<LazyPage><DataAnalyticsPage /></LazyPage>} />
          <Route path="/benchmarking" element={<LazyPage><BenchmarkingPage /></LazyPage>} />
          <Route path="/glossary" element={<LazyPage><MetricGlossaryPage /></LazyPage>} />
          <Route path="/compliance" element={<LazyPage><CompliancePage /></LazyPage>} />
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        {/* Separate Admin Application */}
        <Route
          element={
            <ProtectedRoute roles={["super_admin", "local_admin"]}>
              <LazyPage><AdminLayout /></LazyPage>
            </ProtectedRoute>
          }
        >
          <Route path="/admin/dashboard" element={<LazyPage><AdminDashboard /></LazyPage>} />
          <Route path="/admin/users" element={<LazyPage><AdminUsers /></LazyPage>} />
          <Route path="/admin/accounts" element={<LazyPage><AdminAccounts /></LazyPage>} />
          <Route path="/admin/accounts/:accountId" element={<LazyPage><AdminAccountDetail /></LazyPage>} />
          <Route path="/admin/branding" element={<LazyPage><AdminBranding /></LazyPage>} />
          <Route path="/admin/tickets" element={<LazyPage><AdminTickets /></LazyPage>} />
          <Route path="/admin/audit" element={<LazyPage><AdminAudit /></LazyPage>} />
          <Route path="/admin/subscriptions" element={<LazyPage><AdminSubscriptions /></LazyPage>} />
          <Route path="/admin/settings" element={<LazyPage><AdminSettings /></LazyPage>} />
          <Route path="/admin/partners" element={<LazyPage><AdminPartners /></LazyPage>} />
        </Route>

        {/* Partner Portal Application */}
        <Route
          element={
            <ProtectedRoute roles={["consulting_partner", "super_admin"]}>
              <LazyPage><PartnerLayout /></LazyPage>
            </ProtectedRoute>
          }
        >
          <Route path="/partner/dashboard" element={<LazyPage><PartnerDashboard /></LazyPage>} />
          <Route path="/partner/accounts" element={<LazyPage><PartnerAccounts /></LazyPage>} />
          <Route path="/partner/accounts/:accountId" element={<LazyPage><PartnerAccountView /></LazyPage>} />
          <Route path="/partner/reports" element={<LazyPage><PartnerReports /></LazyPage>} />
          <Route path="/partner/subscription" element={<LazyPage><PartnerSubscription /></LazyPage>} />
          <Route path="/partner/support" element={<LazyPage><PartnerSupport /></LazyPage>} />
          <Route path="/partner/settings" element={<LazyPage><PartnerSettings /></LazyPage>} />
        </Route>
        <Route path="/partner" element={<Navigate to="/partner/dashboard" replace />} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </GlossaryDrawerProvider>
    </>
  );
}

function App() {
  const [betaUnlocked, setBetaUnlocked] = useState(hasBetaAccess());

  // Allow the public developer surface to bypass the beta gate so partners
  // can browse the metric registry without a beta code.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (!betaUnlocked && !path.startsWith("/developer/")) {
    return <BetaGatePage onAccess={() => setBetaUnlocked(true)} />;
  }

  return (
    <Provider store={store}>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="light" position="top-right" richColors expand={false} />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
